import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["trigger", "dropdown", "badge", "list"]

  connect() {
    this.isOpen = false
    this.loaded = false
    this.boundClose = this.closeOnClickOutside.bind(this)
    document.addEventListener("click", this.boundClose)
    this.fetchUnreadCount()
  }

  disconnect() {
    document.removeEventListener("click", this.boundClose)
  }

  toggle(event) {
    event.stopPropagation()
    this.isOpen = !this.isOpen
    this.dropdownTarget.classList.toggle("hidden", !this.isOpen)
    if (this.isOpen && !this.loaded) {
      this.fetchNotifications()
    }
  }

  closeOnClickOutside(event) {
    if (this.isOpen && !this.element.contains(event.target)) {
      this.isOpen = false
      this.dropdownTarget.classList.add("hidden")
    }
  }

  fetchUnreadCount() {
    fetch("/api/notifications", {
      headers: { "X-Requested-With": "XMLHttpRequest" }
    })
    .then(r => r.json())
    .then(data => {
      if (data.unread_count > 0) {
        this.badgeTarget.textContent = data.unread_count > 9 ? "9+" : data.unread_count
        this.badgeTarget.classList.remove("hidden")
        this.badgeTarget.classList.add("flex")
      }
    })
    .catch(() => {})
  }

  fetchNotifications() {
    fetch("/api/notifications", {
      headers: { "X-Requested-With": "XMLHttpRequest" }
    })
    .then(r => r.json())
    .then(data => {
      this.loaded = true
      if (data.notifications.length === 0) {
        this.listTarget.innerHTML = '<div class="p-6 text-center text-sm text-gray-500 dark:text-gray-400">No notifications yet</div>'
        return
      }

      var icons = {
        trophy: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"/></svg>',
        fire: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z"/></svg>',
        star: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"/></svg>',
        lightning: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>',
        bell: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"/></svg>'
      }

      var iconColors = {
        badge_earned: "text-yellow-500 bg-yellow-100 dark:bg-yellow-900/30",
        streak: "text-orange-500 bg-orange-100 dark:bg-orange-900/30",
        xp_milestone: "text-violet-600 bg-violet-100 dark:bg-violet-900/30",
        level_up: "text-indigo-600 bg-indigo-100 dark:bg-indigo-900/30"
      }

      var html = data.notifications.map(function(n) {
        var iconSvg = icons[n.icon] || icons.bell
        var colorClass = iconColors[n.type] || "text-gray-500 bg-gray-100 dark:bg-gray-900/30"
        var unreadDot = n.read ? "" : '<div class="w-2 h-2 bg-violet-600 rounded-full flex-shrink-0"></div>'
        return '<a href="' + (n.url || "/profile") + '" data-notification-id="' + n.id + '" class="notification-item flex items-start gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors cursor-pointer ' + (n.read ? '' : 'bg-violet-50/60 dark:bg-violet-950/30') + '">' +
          '<div class="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ' + colorClass + '">' + iconSvg + '</div>' +
          '<div class="flex-1 min-w-0">' +
            '<p class="text-sm font-medium text-gray-900 dark:text-white truncate">' + n.title + '</p>' +
            '<p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">' + n.time_ago + '</p>' +
          '</div>' +
          unreadDot +
        '</a>'
      }).join("")

      this.listTarget.innerHTML = html

      var self = this
      this.listTarget.querySelectorAll(".notification-item").forEach(function(link) {
        link.addEventListener("click", function(e) {
          var id = link.getAttribute("data-notification-id")
          if (id) self.markSingleRead(id)
        })
      })
    })
    .catch(() => {
      this.listTarget.innerHTML = '<div class="p-4 text-center text-sm text-red-500">Failed to load</div>'
    })
  }

  markSingleRead(id) {
    var badge = this.badgeTarget
    fetch("/api/notifications/" + id + "/mark_read", {
      method: "POST",
      headers: {
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
        "X-Requested-With": "XMLHttpRequest"
      }
    })
    .then(r => r.json())
    .then(data => {
      if (data.unread_count !== undefined) {
        if (data.unread_count > 0) {
          badge.textContent = data.unread_count > 9 ? "9+" : data.unread_count
          badge.classList.remove("hidden")
          badge.classList.add("flex")
        } else {
          badge.classList.add("hidden")
          badge.classList.remove("flex")
        }
      }
    })
  }

  markAllRead() {
    fetch("/api/notifications/mark_read", {
      method: "POST",
      headers: {
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
        "X-Requested-With": "XMLHttpRequest"
      }
    })
    .then(() => {
      this.badgeTarget.classList.add("hidden")
      this.badgeTarget.classList.remove("flex")
      var dots = this.listTarget.querySelectorAll(".bg-violet-600.rounded-full")
      dots.forEach(function(d) { d.remove() })
      var unreadBgs = this.listTarget.querySelectorAll(".bg-violet-50\\/60, .dark\\:bg-violet-950\\/30")
      unreadBgs.forEach(function(el) { el.classList.remove("bg-violet-50/60", "dark:bg-violet-950/30") })
    })
  }
}
